import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ExternalLink, GitBranch, Layers, Loader2, TrendingUp } from 'lucide-react';
import apiClient from '../../../api/client';
import { buildHubUrl } from '../../../config/externalUrls';
import { useChemistryStore } from '../../../stores/chemistryStore';
import {
  ActivityConfiguration,
  ActivityDirection,
  ActivityTransform,
  ActivityUnit,
  MeasurementType,
  SARSourceRow,
  buildActivityPreview,
  buildAnalysisConfiguration,
  detectNumericPropertyColumns,
} from './sarActivity';

interface RGroupEntry {
  name: string;
  smiles: string;
  activity: number;
  Core: string;
  [key: string]: any;
}

interface SARTableResult {
  scaffold: string;
  num_matched: number;
  num_total: number;
  rgroup_columns: string[];
  table: RGroupEntry[];
  analysis_config?: Record<string, unknown>;
}

interface ActivityCliff {
  name_i: string;
  name_j: string;
  smiles_i: string;
  smiles_j: string;
  activity_i: number;
  activity_j: number;
  similarity: number;
  delta_activity: number;
  sali: number;
}

interface CliffsResult {
  cliff_pairs: ActivityCliff[];
  num_compounds: number;
  analysis_config?: Record<string, unknown>;
}

interface ScaffoldEntry {
  scaffold: string;
  count: number;
  generic: string;
}

interface ScaffoldResult {
  scaffolds: ScaffoldEntry[];
  num_compounds: number;
  analysis_config?: Record<string, unknown>;
}

interface SARTabProps {
  smiles: string;
}

const defaultConfiguration: ActivityConfiguration = {
  column: '',
  measurementType: 'IC50',
  unit: 'nM',
  direction: 'lower',
  transform: 'pActivity',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  borderRadius: 6,
  border: '1px solid var(--card-border)',
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text-primary)',
  fontSize: 12,
};

const SARTab: React.FC<SARTabProps> = ({ smiles }) => {
  const { currentFile, totalMolecules } = useChemistryStore();
  const [rows, setRows] = useState<SARSourceRow[] | null>(null);
  const [configuration, setConfiguration] = useState<ActivityConfiguration>(defaultConfiguration);
  const [resultConfiguration, setResultConfiguration] = useState<Record<string, unknown> | null>(null);
  const [sarTable, setSarTable] = useState<SARTableResult | null>(null);
  const [cliffs, setCliffs] = useState<CliffsResult | null>(null);
  const [scaffolds, setScaffolds] = useState<ScaffoldResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hubAvailable, setHubAvailable] = useState<boolean | null>(null);
  const [activeSection, setActiveSection] = useState<'rgroup' | 'cliffs' | 'scaffolds'>('rgroup');

  useEffect(() => {
    apiClient.get('/v2/sar/health', { timeout: 3000 })
      .then(() => setHubAvailable(true))
      .catch(() => setHubAvailable(false));
  }, []);

  const numericColumns = useMemo(() => detectNumericPropertyColumns(rows || []), [rows]);
  const preview = useMemo(
    () => configuration.column && rows ? buildActivityPreview(rows, configuration) : null,
    [configuration, rows],
  );

  const loadDataset = useCallback(async () => {
    if (!currentFile?.id) return;
    if (totalMolecules > 500) {
      setError(`Embedded SAR beta validates complete files up to 500 compounds. This file has ${totalMolecules}; use the standalone/server-scale workflow rather than analysing a partial dataset.`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const molRes = await apiClient.post(`/files/${currentFile.id}/molecules/query`, {
        start_row: 0,
        end_row: Math.max(totalMolecules, 3),
        sort_model: [],
        filter_model: {},
      });
      const loadedRows = (molRes.data?.rows || []) as SARSourceRow[];
      setRows(loadedRows);
      const columns = detectNumericPropertyColumns(loadedRows);
      setConfiguration((current) => ({
        ...current,
        column: columns.includes(current.column) ? current.column : '',
      }));
      if (loadedRows.length === 0) setError('No compounds are available in the current file.');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load compounds for SAR configuration');
    } finally {
      setLoading(false);
    }
  }, [currentFile?.id, totalMolecules]);

  const runActivityAnalysis = useCallback(async () => {
    if (!currentFile?.id || !rows || !configuration.column || !preview) {
      setError('Select a valid activity column before running activity-dependent analysis.');
      return;
    }
    if (preview.valid < 3) {
      setError('Activity-dependent SAR requires at least 3 valid compounds after exclusions.');
      return;
    }
    setLoading(true);
    setError(null);
    const analysisConfig = buildAnalysisConfiguration(configuration, preview, currentFile.id, rows.length);
    try {
      const [tableRes, cliffsRes] = await Promise.all([
        apiClient.post('/v2/sar/sar/table', { compounds: preview.compounds, analysis_config: analysisConfig }),
        apiClient.post('/v2/sar/sar/cliffs', { compounds: preview.compounds, analysis_config: analysisConfig }),
      ]);
      setSarTable(tableRes.data);
      setCliffs(cliffsRes.data);
      setScaffolds(null);
      setResultConfiguration(tableRes.data.analysis_config || analysisConfig);
      setActiveSection('rgroup');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to run activity-dependent SAR analysis');
    } finally {
      setLoading(false);
    }
  }, [configuration, currentFile?.id, preview, rows]);

  const runScaffoldAnalysis = useCallback(async () => {
    if (!currentFile?.id || !rows) return;
    const smilesList = rows.map((row) => row.smiles).filter((value): value is string => Boolean(value));
    if (smilesList.length < 3) {
      setError('Scaffold analysis requires at least 3 compounds with valid structures.');
      return;
    }
    const analysisConfig = {
      mode: 'activity-independent',
      source_file_id: String(currentFile.id),
      valid_structure_count: smilesList.length,
      excluded_structure_count: rows.length - smilesList.length,
      source_row_count: rows.length,
    };
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/v2/sar/scaffold/network', {
        smiles_list: smilesList,
        analysis_config: analysisConfig,
      });
      setSarTable(null);
      setCliffs(null);
      setScaffolds(response.data);
      setResultConfiguration(response.data.analysis_config || analysisConfig);
      setActiveSection('scaffolds');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to run scaffold analysis');
    } finally {
      setLoading(false);
    }
  }, [currentFile?.id, rows]);

  useEffect(() => {
    setRows(null);
    setSarTable(null);
    setCliffs(null);
    setScaffolds(null);
    setResultConfiguration(null);
    setError(null);
    setConfiguration(defaultConfiguration);
  }, [smiles, currentFile?.id]);

  if (hubAvailable === false) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
        <GitBranch size={32} style={{ color: 'var(--text-muted)' }} />
        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>SAR Analysis</p>
        <p className="text-xs max-w-xs" style={{ color: 'var(--text-muted)' }}>
          Embedded SAR is not deployed in this environment. The standalone SAR Hub may be available separately.
        </p>
        <span className="px-3 py-1 rounded-full text-[11px] font-medium" style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-muted)' }}>
          Not available here
        </span>
      </div>
    );
  }

  if (totalMolecules > 0 && totalMolecules < 3) {
    return <div className="text-center py-12 text-sm" style={{ color: 'var(--text-secondary)' }}>SAR analysis requires at least 3 compounds.</div>;
  }

  if (!rows) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
        <GitBranch size={24} style={{ color: 'var(--text-muted)' }} />
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Configure a scientifically traceable SAR analysis</p>
        <p className="text-xs max-w-sm" style={{ color: 'var(--text-muted)' }}>
          Activity-dependent analysis requires an explicitly selected numeric column. Scaffold-only analysis does not require activity.
        </p>
        {error && <p role="alert" className="text-xs" style={{ color: '#dc2626' }}>{error}</p>}
        <button onClick={loadDataset} disabled={loading} className="px-4 py-2 rounded-lg text-xs font-medium" style={{ backgroundColor: '#0d9488', color: 'white' }}>
          {loading ? 'Loading…' : 'Configure SAR'}
        </button>
      </div>
    );
  }

  if (!resultConfiguration) {
    const update = <K extends keyof ActivityConfiguration>(key: K, value: ActivityConfiguration[K]) =>
      setConfiguration((current) => ({ ...current, [key]: value }));
    return (
      <div className="space-y-4 p-1" data-testid="sar-configuration">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>SAR activity configuration</h3>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            Select and confirm the scientific meaning of the values. No missing or invalid value will be inferred or replaced.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Activity column *
            <select aria-label="Activity column" value={configuration.column} onChange={(e) => update('column', e.target.value)} style={selectStyle}>
              <option value="">Select a numeric column…</option>
              {numericColumns.map((column) => <option key={column} value={column}>{column}</option>)}
            </select>
          </label>
          <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Measurement type
            <select aria-label="Measurement type" value={configuration.measurementType} onChange={(e) => update('measurementType', e.target.value as MeasurementType)} style={selectStyle}>
              {['IC50', 'EC50', 'Ki', 'Kd', 'pActivity', 'Other'].map((type) => <option key={type}>{type}</option>)}
            </select>
          </label>
          <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Unit
            <select aria-label="Activity unit" value={configuration.unit} onChange={(e) => update('unit', e.target.value as ActivityUnit)} style={selectStyle}>
              {['M', 'mM', 'uM', 'nM', 'pM', 'unitless'].map((unit) => <option key={unit} value={unit}>{unit === 'uM' ? 'µM' : unit}</option>)}
            </select>
          </label>
          <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Stronger activity
            <select aria-label="Activity direction" value={configuration.direction} onChange={(e) => update('direction', e.target.value as ActivityDirection)} style={selectStyle}>
              <option value="lower">Lower values are stronger</option>
              <option value="higher">Higher values are stronger</option>
            </select>
          </label>
          <label className="text-xs col-span-2" style={{ color: 'var(--text-secondary)' }}>
            Transformation
            <select aria-label="Activity transformation" value={configuration.transform} onChange={(e) => update('transform', e.target.value as ActivityTransform)} style={selectStyle}>
              <option value="none">None (direction-normalised for analysis)</option>
              <option value="pActivity">Convert molar potency to pActivity (-log10 M)</option>
            </select>
          </label>
        </div>
        {numericColumns.length === 0 && (
          <div role="alert" className="text-xs p-2 rounded" style={{ backgroundColor: '#fffbeb', color: '#92400e' }}>
            No usable numeric property columns were detected. Activity-dependent analysis is unavailable.
          </div>
        )}
        {preview && (
          <div className="grid grid-cols-4 gap-2" data-testid="activity-preview">
            {([['Valid', preview.valid], ['Missing', preview.missing], ['Non-numeric', preview.nonNumeric], ['Excluded', preview.excluded]] as const).map(([label, count]) => (
              <div key={label} className="rounded border p-2 text-center" style={{ borderColor: 'var(--card-border)' }}>
                <div className="text-base font-semibold" style={{ color: label === 'Valid' ? '#0d9488' : 'var(--text-primary)' }}>{count}</div>
                <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</div>
              </div>
            ))}
          </div>
        )}
        {error && <div role="alert" className="text-xs p-2 rounded" style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }}>{error}</div>}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={runActivityAnalysis}
            disabled={loading || !configuration.column || !preview || preview.valid < 3}
            className="px-3 py-2 rounded text-xs font-medium disabled:opacity-40"
            style={{ backgroundColor: '#0d9488', color: 'white' }}
          >
            {loading ? 'Running…' : 'Run activity-dependent SAR'}
          </button>
          <button
            onClick={runScaffoldAnalysis}
            disabled={loading}
            className="px-3 py-2 rounded text-xs font-medium"
            style={{ border: '1px solid #0d9488', color: '#0d9488' }}
          >
            Run activity-independent scaffold analysis
          </button>
        </div>
      </div>
    );
  }

  const sections = [
    ...(sarTable ? [{ id: 'rgroup' as const, label: 'R-Groups', icon: <Layers size={12} /> }] : []),
    ...(cliffs ? [{ id: 'cliffs' as const, label: 'Activity Cliffs', icon: <TrendingUp size={12} /> }] : []),
    ...(scaffolds ? [{ id: 'scaffolds' as const, label: 'Scaffolds', icon: <GitBranch size={12} /> }] : []),
  ];

  return (
    <div className="space-y-3">
      <div className="rounded border p-2 text-[10px]" style={{ borderColor: 'var(--card-border)', color: 'var(--text-secondary)' }} data-testid="analysis-provenance">
        <div className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
          {resultConfiguration.mode === 'activity-independent' ? 'Activity-independent scaffold analysis' : 'Validated activity-dependent analysis'}
        </div>
        {Object.entries(resultConfiguration).map(([key, value]) => <span key={key} className="mr-3">{key.replaceAll('_', ' ')}: {String(value)}</span>)}
      </div>
      <div className="flex justify-between">
        <button onClick={() => setResultConfiguration(null)} className="text-xs" style={{ color: '#0d9488' }}>New analysis</button>
        <button onClick={() => window.open(buildHubUrl('sar', { smiles }), '_blank')} className="flex items-center gap-1 text-xs" style={{ color: '#0d9488' }}>
          <ExternalLink size={11} /> Open standalone SAR Hub
        </button>
      </div>
      {error && <div role="alert" className="flex items-center gap-2 text-xs" style={{ color: '#b91c1c' }}><AlertTriangle size={13} />{error}</div>}
      <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)' }}>
        {sections.map((section) => (
          <button key={section.id} onClick={() => setActiveSection(section.id)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs" style={{ color: activeSection === section.id ? '#0d9488' : 'var(--text-secondary)' }}>
            {section.icon} {section.label}
          </button>
        ))}
      </div>
      {activeSection === 'rgroup' && sarTable && (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--card-border)' }}>
          <div className="p-2 text-xs" style={{ color: 'var(--text-muted)' }}>Scaffold matched {sarTable.num_matched}/{sarTable.num_total} validated compounds</div>
          <table className="w-full text-xs"><thead><tr><th className="p-2 text-left">Compound</th><th className="p-2 text-right">Analysis activity</th>{sarTable.rgroup_columns.map((column) => <th key={column} className="p-2 text-left">{column}</th>)}</tr></thead>
            <tbody>{sarTable.table.map((row, index) => <tr key={index}><td className="p-2">{row.name}</td><td className="p-2 text-right font-mono">{row.activity.toFixed(3)}</td>{sarTable.rgroup_columns.map((column) => <td key={column} className="p-2 font-mono text-[10px]">{row[column] || '—'}</td>)}</tr>)}</tbody>
          </table>
        </div>
      )}
      {activeSection === 'cliffs' && cliffs && (
        <div className="space-y-2">
          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{cliffs.cliff_pairs.length} activity cliffs detected</div>
          {cliffs.cliff_pairs.slice(0, 20).map((cliff, index) => (
            <div key={index} className="rounded-lg border p-3 text-xs" style={{ borderColor: 'var(--card-border)' }}>
              <div className="font-medium">{cliff.name_i} ↔ {cliff.name_j}</div>
              <div style={{ color: 'var(--text-muted)' }}>Similarity {(cliff.similarity * 100).toFixed(0)}% · Δ {cliff.delta_activity.toFixed(3)} · SALI {cliff.sali.toFixed(3)}</div>
            </div>
          ))}
          {cliffs.cliff_pairs.length === 0 && <div className="text-center py-6 text-xs">No significant activity cliffs detected</div>}
        </div>
      )}
      {activeSection === 'scaffolds' && scaffolds && (
        <div className="space-y-2">
          <div className="text-xs font-medium" style={{ color: '#0d9488' }}>Activity-independent: no potency values were used.</div>
          <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--card-border)' }}>
            <table className="w-full text-xs"><thead><tr><th className="p-2 text-left">Scaffold</th><th className="p-2 text-right">Count</th><th className="p-2 text-left">Generic framework</th></tr></thead>
              <tbody>{scaffolds.scaffolds.map((entry, index) => <tr key={index}><td className="p-2 font-mono text-[10px]">{entry.scaffold || 'Acyclic'}</td><td className="p-2 text-right">{entry.count}</td><td className="p-2 font-mono text-[10px]">{entry.generic || '—'}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
      )}
      {loading && <div className="flex items-center gap-2 text-xs"><Loader2 className="w-4 h-4 animate-spin" /> Running analysis…</div>}
    </div>
  );
};

export default SARTab;
